/**
 * The vocabulary and derivations shared by «مدیریت هزینه‌ها» and «کالاها و خدمات».
 *
 * FOUR surfaces import this file, not two: each of the two workbenches has a
 * desktop variant and a mobile reduction, and they render nothing alike — that is
 * the doctrine (clinic-app/CLAUDE.md §4.1: `.web.jsx` is canonical, `.mobile.jsx`
 * is a reduction of it). What they must NEVER do is disagree about what a thing is
 * called, which rail entries a person may see, or what the totals add up to — so
 * every label set, every permission gate and every derivation lives here and is
 * imported by all of them.
 *
 * It still lives under `screens/Operations/` after the split
 * (docs/CLINIC_EXPENSES_AND_GOODS_SPLIT_DESIGN.md §12 D5): two other screens
 * already import it across folder boundaries (`hooks/useEquipment.js`,
 * `Clinic/components/InventoryTierView.jsx`), so a cross-folder import of it is
 * the codebase's existing shape rather than a new smell. Promoting it to
 * `src/lib/` is a thirty-import rename with no behaviour change, deliberately
 * deferred so the split reads as a move rather than a rewrite.
 *
 * Nothing in this file renders. No React, no JSX, no tokens.
 */

import { isIranMarket } from '@parsina/framework/ui/lib/brand';
import { toDisplay } from '../../lib/currency';
import { msToJalali, jalaliToMs } from '../../lib/jalali';
import { gregorianBooks, monthStartMs } from '../../lib/dateRange';
import equipmentTransitions from '../../constants/equipmentTransitions.json';
import { permFor as reportPermFor } from '../Reports/Reports.shared';
import {
  EXPENSES_VIEWS as EXPENSES_VIEW_ORDER,
  GOODS_VIEWS as GOODS_VIEW_ORDER,
  pageForView,
} from '../../constants/moduleViews';

// ── The rails: what each module is made of ───────────────────────────────
//
// TWO rails, because «عملیات و هزینه» became two pages on 2026-09-12
// (docs/CLINIC_EXPENSES_AND_GOODS_SPLIT_DESIGN.md). One rail held sixteen
// destinations answering two different questions — what the clinic SPENT, and
// what it STOCKS AND OFFERS — and a rail that long stops being a switcher and
// becomes a second navigation.
//
// Each rail keeps the two-group split it had inside the merged module: the work
// people do every day, and the setup they touch a few times a year. That split
// is the argument for a rail over a tab strip: tabs can hold six items but
// cannot say that some of them are a different KIND of thing, and the accountant
// who lives in دفتر هزینه should not have مراکز هزینه at the same visual weight
// beside it.
//
// `perm` is the permission that reveals an entry. A مسئول انبار holding only
// inventory.* sees exactly two entries on the goods rail, and the page honestly
// degrades to those two lists — which is correct, not a failure. Gating here
// (rather than in the shell) is what keeps a page's two surfaces from drifting
// on who sees what.
//
// WHICH PAGE an entry lives on is not recorded here — it is in
// `src/constants/moduleViews.js`, which has no imports and is therefore cheap
// for the router and the skeleton registry to read before any screen chunk
// loads. The rails are checked against it in `Operations.shared.test.js`, so
// the two cannot drift: a destination added to one and forgotten in the other
// fails the suite rather than shipping a view nothing can link to.

/** «مدیریت هزینه‌ها» — money out, and the definitions that classify it. */
export const EXPENSES_RAIL_GROUPS = (t) => ([
  {
    key: 'daily',
    labelFa: t('operations.rail.dailyWork'),
    items: [
      {
        key: 'expenses',
        labelFa: t('operations.views.expenses'),
        perm: 'expenses.view',
        // Legacy route this view replaces — used to redirect old links and bookmarks.
        legacyPath: '/expenses',
      },
      {
        // «قبوض» is daily work, not setup: what is overdue is a question someone
        // asks every morning, while «انواع قبض» is touched twice a year.
        key: 'bills',
        labelFa: t('operations.views.bills'),
        perm: 'expenses.view',
      },
    ],
  },
  {
    key: 'setup',
    labelFa: t('operations.rail.definitions'),
    items: [
      { key: 'recurring', labelFa: t('operations.views.recurring'), perm: 'expenses.view' },
      { key: 'billTypes', labelFa: t('operations.views.billTypes'), perm: 'expenses.view' },
    ],
  },
]);

/** «کالاها و خدمات» — what the clinic stocks, what it offers, what it owns. */
export const GOODS_RAIL_GROUPS = (t) => ([
  {
    key: 'daily',
    labelFa: t('operations.rail.dailyWork'),
    items: [
      {
        key: 'services',
        labelFa: t('operations.views.services'),
        perm: 'services.view',
        legacyPath: '/services',
      },
      {
        key: 'items',
        labelFa: t('operations.views.items'),
        perm: 'inventory.view',
        legacyPath: '/inventory',
      },
      {
        // «تجهیزات» is daily work for the same reason «کالاها» is: what needs a
        // service TODAY is a question the keeper asks every morning. Its own key,
        // not inventory.view — a device is not stock (equipment design §B.4) —
        // but it IS a physical good the clinic owns, its drawer consumes `items`
        // for spare parts and `suppliers` for the vendor, and the persona who
        // runs it is مسئول انبار. That is why the register came to this page
        // rather than staying beside the ledger it posts to (split design §12 D1).
        key: 'equipment',
        labelFa: t('operations.views.equipment'),
        perm: 'equipment.view',
      },
    ],
  },
  {
    key: 'setup',
    labelFa: t('operations.rail.definitions'),
    items: [
      { key: 'suppliers', labelFa: t('operations.views.suppliers'), perm: 'inventory.view' },
      // The definitions a device is an instance of. Touched a few times a year,
      // and only by somebody who may register devices.
      { key: 'equipmentTypes', labelFa: t('operations.views.equipmentTypes'), perm: 'equipment.manage' },
      // ── The lifecycle entries ────────────────────────────────────
      //
      // `cap` is the CLINIC's inventory capability that reveals the entry, on top
      // of the permission that reveals it to this person. A «ساده» clinic has no
      // capabilities at all and therefore sees none of these — which is the whole
      // of progressive complexity in the rail: not a different screen, the same
      // screen with fewer destinations.
      {
        key: 'expiry', labelFa: t('operations.views.expiry'),
        perm: 'inventory.view', cap: 'expiry',
      },
      {
        key: 'procurement', labelFa: t('operations.views.procurement'),
        perm: 'inventory.view', cap: 'procurement',
      },
      {
        key: 'custody', labelFa: t('operations.views.custody'),
        perm: 'inventory.view', cap: 'custody',
      },
      {
        key: 'counts', labelFa: t('operations.views.counts'),
        perm: 'inventory.view', cap: 'counts',
      },
      {
        key: 'locations', labelFa: t('operations.views.locations'),
        perm: 'inventory.view', cap: 'locations',
      },
      // `contracts.manage`, not `services.view`: this screen is the clinic↔insurer
      // COMMERCIAL AGREEMENT, the same kind of thing as a doctor's contract, and
      // the warehouse-keeper template holds `services.view` so it can see the
      // service list for stock. Gating on that would put the clinic's insurance
      // terms in front of مسئول انبار.
      //
      // It sits on the GOODS page because it is service pricing seen from the
      // insurer's side: the view's own header calls it «what each service costs
      // the patient under that agreement», and its picker is fed by the services
      // the clinic declares — a rule can never name a service it does not
      // perform (split design §12 D2).
      //
      // Reading a coverage rule to EXPLAIN a share is a different question and
      // stays on `services.view` at the BFF, so the settle drawer's ⓘ still works
      // for a منشی who can never open this editor.
      { key: 'insurance', labelFa: t('insurance.title'), perm: 'contracts.manage' },
    ],
  },
]);

/**
 * Both rails, concatenated.
 *
 * Not something a page renders — it exists so the partition can be ASSERTED:
 * every destination the merged module had is on exactly one of the two rails,
 * none lost and none duplicated. A test reading this is the only thing that
 * makes "eleven views moved" checkable rather than merely reviewable. It is also
 * the safe default for `visibleRail`/`resolveView` (see below).
 */
export const RAIL_GROUPS = (t) => ([...EXPENSES_RAIL_GROUPS(t), ...GOODS_RAIL_GROUPS(t)]);

/**
 * Every rail destination, flattened — the STRUCTURE only.
 *
 * Read through a translator that returns the key it is handed, because nothing
 * here needs the labels: the key lists validate a `?view=` parameter, and
 * `viewForLegacyPath`/`pageForView` answer which page owns a view. Those are all
 * answers about the URL, and a URL has no language. Threading a real `t` in
 * would have made module constants into functions and every reader into a call,
 * for labels none of them reads.
 */
const flatten = (groups) => groups((key) => key).flatMap((g) => g.items);

const ALL_VIEWS = [...flatten(EXPENSES_RAIL_GROUPS), ...flatten(GOODS_RAIL_GROUPS)];

/**
 * The key lists, re-exported from the routing module rather than recomputed.
 *
 * Recomputing them from the rails would give two answers to one question and
 * make «which views moved?» depend on which module you happened to import. The
 * rails stay the place a destination is DECLARED (label, permission, tier); the
 * lists stay the place it is ADDRESSED. The test holds them equal.
 */
export { EXPENSES_VIEW_ORDER as EXPENSES_VIEW_KEYS, GOODS_VIEW_ORDER as GOODS_VIEW_KEYS, pageForView };
export const VIEW_KEYS = [...EXPENSES_VIEW_ORDER, ...GOODS_VIEW_ORDER];

/**
 * The rail as this person may see it, in this clinic.
 *
 * TWO gates, and they answer different questions: `can(perm)` is whether this
 * PERSON may see the destination, `caps` is whether this CLINIC has turned the
 * capability on. An entry needs both, and an entry with no `cap` needs only the
 * first — that is what keeps «کالاها» and «تأمین‌کنندگان» present for every clinic
 * while the five lifecycle screens appear only where they mean something.
 *
 * `caps` defaults to empty, which is «ساده» — the safe reading for a caller that
 * has not loaded the settings yet, because it reveals nothing rather than
 * flashing screens that then disappear.
 *
 * `groups` is the rail to filter, and each page passes its own. It defaults to
 * BOTH rails rather than to either one: a caller that forgets the argument then
 * gets a rail that is too long, which is visible on the first render, instead of
 * one silently missing half its destinations.
 */
export function visibleRail(can, t, caps = [], groups = RAIL_GROUPS) {
  const enabled = new Set(caps || []);
  return groups(t)
    .map((g) => ({
      ...g,
      items: g.items.filter((i) => can(i.perm) && (!i.cap || enabled.has(i.cap))),
    }))
    .filter((g) => g.items.length);
}

/**
 * Which view to open.
 *
 * Falls back to the first entry this person may see rather than to a fixed default:
 * landing a مسئول انبار on an empty دفتر هزینه would teach them the module is broken.
 * Returns null when they may see nothing at all, which the shell renders as a
 * permission empty-state rather than a blank page.
 */
export function resolveView(requested, can, t, caps = [], groups = RAIL_GROUPS) {
  const visible = visibleRail(can, t, caps, groups);
  const allowed = visible.flatMap((g) => g.items.map((i) => i.key));
  if (requested && allowed.includes(requested)) return requested;
  return allowed[0] || null;
}

/**
 * The page and view a legacy route redirects into (`/inventory` →
 * `/goods?view=items`).
 *
 * Returns a `{ page, view }` pair rather than a bare view id, because after the
 * split the three legacy paths no longer land on one page. A caller that only
 * wants the view reads `.view`; nobody has to remember which page owns which.
 */
export function viewForLegacyPath(path) {
  const hit = ALL_VIEWS.find((v) => v.legacyPath === path);
  return hit ? { page: pageForView(hit.key), view: hit.key } : null;
}

// ── Expense taxonomy ─────────────────────────────────────────────────────────
// Mirrors EXPENSE_CATEGORIES in the accounting service's app/domain/expenses.py.
// `tone` names a semantic role rather than a colour: the screen resolves it through
// the framework tokens, so no hex is written here (packages/DESIGN.md rule 1).

export const EXPENSE_CATEGORIES = (t) => ([
  { key: 'personnel', labelFa: t('operations.expenseCategories.payroll'), tone: 'info' },
  { key: 'operating', labelFa: t('operations.expenseCategories.operating'), tone: 'accent' },
  { key: 'equipment', labelFa: t('operations.expenseCategories.equipment'), tone: 'violet' },
  { key: 'consumable', labelFa: t('operations.expenseCategories.consumables'), tone: 'warn' },
  { key: 'depreciation', labelFa: t('operations.expenseCategories.depreciation'), tone: 'mute' },
  { key: 'other', labelFa: t('common.state.other'), tone: 'mute' },
]);

export const categoryLabel = (key, t) =>
  EXPENSE_CATEGORIES(t).find((c) => c.key === key)?.labelFa || key || '—';

/**
 * Cost centres are a SYSTEM-MANAGED dimension and live in lib/costCenter.js.
 *
 * There is no list of «kinds» here any more because there is nothing to make: the
 * centre is derived from where a cost arose, out of a platform vocabulary shared
 * byte-for-byte with the accounting service
 * (docs/CLINIC_COST_CENTER_AUTO_MAPPING_DESIGN.md). What a screen needs from it is
 * `costCenterLabel`, `costCenterOptions` and `previewCostCenterForCategory`.
 */

/**
 * Where an expense came from. Only `manual` is typed by a person; the rest were
 * posted by something, and saying which is what lets an accountant tell «چرا این
 * ردیف هست؟» without opening it.
 */
export const SOURCE_LABELS = (t) => ({
  manual: '',                       // the default — no chip, or every row wears one
  recurring: t('operations.expenseSources.recurring'),
  inventory_purchase: t('operations.expenseSources.purchase'),
  hr_payroll: t('operations.expenseSources.payroll'),
  depreciation_accrual: t('operations.expenseCategories.depreciation'),
  inventory_withdrawal: t('operations.expenseSources.stockOut')
});

export const sourceLabel = (key, t) => SOURCE_LABELS(t)[key] ?? '';

// ── Approval ─────────────────────────────────────────────────────────────────
// Orthogonal to `status`, and the two must never be shown as one thing: a void row
// was posted in error, a rejected row was a real request the clinic turned down.

export const APPROVAL_STATES = (t) => ([
  { key: 'pending', labelFa: t('operations.approval.pending'), tone: 'warn' },
  { key: 'approved', labelFa: t('operations.approval.approved'), tone: 'good' },
  { key: 'rejected', labelFa: t('operations.approval.rejected'), tone: 'danger' },
]);

export const approvalLabel = (key, t) =>
  APPROVAL_STATES(t).find((s) => s.key === key)?.labelFa || key || '—';

export const approvalTone = (key, t) =>
  APPROVAL_STATES(t).find((s) => s.key === key)?.tone || 'mute';

/** Does this row still need a decision? Void rows never do — they left the books. */
export const needsRuling = (expense) =>
  !!expense && expense.status !== 'void' && expense.approvalStatus === 'pending';

// ── Inventory ────────────────────────────────────────────────────────────────

export const ITEM_CATEGORIES = (t) => ([
  { key: 'consumable', labelFa: t('operations.expenseCategories.consumables') },
  { key: 'device', labelFa: t('operations.serviceGroups.device') },
  { key: 'pharma', labelFa: t('patients.medication.entity') },
  { key: 'orthopedic', labelFa: t('operations.serviceGroups.orthopaedics') },
]);

export const itemCategoryLabel = (key, t) =>
  ITEM_CATEGORIES(t).find((c) => c.key === key)?.labelFa || key || '—';

/**
 * Stock health. `<=` at the minimum on purpose: at exactly the minimum the shelf
 * still needs reordering, which is the whole point of setting one — and it matches
 * how the BFF counts lowStockCount, so the KPI and the rows can never disagree.
 */
export function stockState(item, t) {
  if (!item) return { key: 'ok', labelFa: t('operations.stock.inStock'), tone: 'good' };
  if (item.currentStock <= 0) return { key: 'out', labelFa: t('operations.stock.outOfStock'), tone: 'danger' };
  if (item.currentStock <= item.minimumStock) return { key: 'low', labelFa: t('operations.stock.low'), tone: 'warn' };
  return { key: 'ok', labelFa: t('operations.stock.inStock'), tone: 'good' };
}

/**
 * Why stock moved. Mirrors OUTFLOW_REASONS / INFLOW_REASONS in the warehousing
 * service's app/domain/movements.py — the service REFUSES a negative adjustment with
 * no reason, so a value here that the service does not know is a hard error at save
 * time, not a cosmetic mismatch.
 */
export const OUTFLOW_REASONS = (t) => ([
  { key: 'waste', labelFa: t('operations.stockReasons.waste') },
  { key: 'damage', labelFa: t('operations.stockReasons.damage') },
  { key: 'expired', labelFa: t('operations.stockReasons.expiry') },
  { key: 'internal_use', labelFa: t('operations.stockReasons.internalUse') },
  { key: 'count_correction', labelFa: t('operations.stockReasons.countCorrection') },
  { key: 'return_to_supplier', labelFa: t('operations.stockReasons.returnToSupplier') },
]);

export const INFLOW_REASONS = (t) => ([
  { key: 'count_correction', labelFa: t('operations.stockReasons.countCorrection') },
  { key: 'return_from_use', labelFa: t('operations.stockReasons.returnedUnused') },
  { key: 'opening_balance', labelFa: t('operations.stockReasons.opening') },
]);

export const reasonsFor = (quantity, t) => (quantity < 0 ? OUTFLOW_REASONS(t) : INFLOW_REASONS(t));

export const reasonLabel = (key, t) =>
  [...OUTFLOW_REASONS(t), ...INFLOW_REASONS(t)].find((r) => r.key === key)?.labelFa || key || '';

/**
 * What happened to the stock.
 *
 * The first keys are what the ledger called things before the lifecycle existed.
 * The migration rewrites the stored rows, but a replica still serving an old one
 * must not render it as «نامشخص», so both spellings live here.
 */
export const MOVEMENT_LABELS = (t) => ({
  PURCHASE_IN: t('operations.stockReasons.fromPurchase'),
  MANUAL_ADJUST: t('operations.stockReasons.manualAdjustment'),
  MANUAL_ADJUSTMENT: t('operations.stockReasons.manualAdjustment'),
  RECEIVE: t('operations.stockReasons.fromPurchase'),
  ADJUSTMENT: t('operations.stockReasons.manualAdjustment'),
  CONSUMPTION: t('operations.stockReasons.usedForPatient'),
  TRANSFER_OUT: t('operations.movements.transferOut'),
  TRANSFER_IN: t('operations.movements.transferIn'),
  ISSUE: t('operations.movements.issue'),
  SALE: t('operations.movements.sale'),
  RETURN_IN: t('operations.movements.returnIn'),
  SUPPLIER_RETURN: t('operations.stockReasons.returnToSupplier'),
  DISPOSAL: t('operations.movements.disposal'),
  LOST: t('operations.movements.lost'),
  THEFT: t('operations.movements.theft'),
  RECALL_BLOCK: t('operations.movements.recallBlock'),
  STATE_CHANGE: t('operations.movements.stateChange'),
  OPEN: t('operations.movements.open'),
});

export const movementLabel = (key, t) => MOVEMENT_LABELS(t)[key] || key || '—';

// ── The lifecycle vocabulary ──────────────────────────────────────────
//
// Every set below mirrors one in the warehousing service's app/domain/. The
// service VALIDATES against its copy, so a value here it does not know is a hard
// error at save time rather than a cosmetic mismatch — which is exactly how the
// reason vocabulary has behaved since it was introduced.

/**
 * How deep this clinic's inventory goes.
 *
 * The tier UNLOCKS a capability; a per-item flag decides whether it APPLIES. That
 * is why «ساده» is not a smaller feature set so much as a quieter one: the same
 * ledger, the same screens, fewer of them rendered.
 */
export const INVENTORY_TIERS = (t) => ([
  {
    key: 'simple',
    labelFa: t('operations.tiers.simple'),
    descriptionFa: t('operations.tiers.simpleHint'),
  },
  {
    key: 'standard',
    labelFa: t('operations.tiers.standard'),
    descriptionFa: t('operations.tiers.standardHint'),
  },
  {
    key: 'advanced',
    labelFa: t('operations.tiers.advanced'),
    descriptionFa: t('operations.tiers.advancedHint'),
  },
]);

export const tierLabel = (key, t) =>
  INVENTORY_TIERS(t).find((x) => x.key === key)?.labelFa || key || '';

export const TIER_ORDER = { simple: 0, standard: 1, advanced: 2 };

/**
 * How an item BEHAVES, as opposed to what it is.
 *
 * `category` is the taxonomy a person filters by; this is what the screens branch
 * on — a syringe and a handpiece can share a category and must not share a
 * lifecycle.
 */
export const ITEM_CLASSES = (t) => ([
  { key: 'consumable', labelFa: t('operations.itemClasses.consumable') },
  { key: 'serialized', labelFa: t('operations.itemClasses.serialized') },
  { key: 'reusable', labelFa: t('operations.itemClasses.reusable') },
  { key: 'sellable', labelFa: t('operations.itemClasses.sellable') },
]);

/**
 * What condition the stock is in.
 *
 * Quarantined, blocked, expired and damaged stock is PHYSICALLY THERE and must
 * never read as available — conflating the two is how expired stock reaches a
 * patient.
 */
export const STOCK_STATES = (t) => ([
  { key: 'available', labelFa: t('operations.lotStates.available'), tone: 'good' },
  { key: 'opened', labelFa: t('operations.lotStates.opened'), tone: 'info' },
  { key: 'quarantine', labelFa: t('operations.lotStates.quarantine'), tone: 'warn' },
  { key: 'blocked', labelFa: t('operations.lotStates.blocked'), tone: 'danger' },
  { key: 'expired', labelFa: t('operations.lotStates.expired'), tone: 'danger' },
  { key: 'damaged', labelFa: t('operations.lotStates.damaged'), tone: 'danger' },
  { key: 'in_transit', labelFa: t('operations.lotStates.inTransit'), tone: 'mute' },
]);

export const lotStateLabel = (key, t) =>
  STOCK_STATES(t).find((s) => s.key === key)?.labelFa || key || '—';

export const lotStateTone = (key, t) =>
  STOCK_STATES(t).find((s) => s.key === key)?.tone || 'mute';

/** Where a lot may be sent by hand, and why. */
export const LOT_STATE_ACTIONS = (t) => ([
  { key: 'quarantine', labelFa: t('operations.lotStates.quarantine') },
  { key: 'available', labelFa: t('operations.lotActions.release') },
  { key: 'damaged', labelFa: t('operations.lotStates.damaged') },
  { key: 'blocked', labelFa: t('operations.lotStates.blocked') },
]);

/** Why a lot was quarantined or found damaged — an inspection finding. */
export const INSPECTION_REASONS = (t) => ([
  { key: 'suspect_packaging', labelFa: t('operations.inspection.suspectPackaging') },
  { key: 'damaged_on_arrival', labelFa: t('operations.inspection.damagedOnArrival') },
  { key: 'discrepancy', labelFa: t('operations.inspection.discrepancy') },
  { key: 'cold_chain', labelFa: t('operations.inspection.coldChain') },
  { key: 'batch_problem', labelFa: t('operations.inspection.batchProblem') },
  { key: 'quality_review', labelFa: t('operations.inspection.qualityReview') },
  { key: 'recall', labelFa: t('operations.inspection.recall') },
]);

/** What the clinic is offered when a lot crosses an expiry rung. */
export const EXPIRY_ACTIONS = (t) => ({
  flag: t('operations.expiry.actionFlag'),
  consume_first: t('operations.expiry.actionConsumeFirst'),
  transfer: t('operations.expiry.actionTransfer'),
  discount_or_return: t('operations.expiry.actionDiscountOrReturn'),
  prepare_disposal: t('operations.expiry.actionPrepareDisposal'),
  expired: t('operations.lotStates.expired'),
});

export const expiryActionLabel = (key, t) => EXPIRY_ACTIONS(t)[key] || '';

/**
 * How urgent an expiry is. The tone follows the RUNG, not the raw day count: a
 * lot 40 days out and one 80 days out are the same decision, and colouring them
 * differently would suggest otherwise.
 */
export function expiryTone(daysLeft) {
  if (daysLeft == null) return 'mute';
  if (daysLeft <= 30) return 'danger';
  if (daysLeft <= 90) return 'warn';
  return 'info';
}

/** Kinds of place stock can be. `custodian` is a person, and that is deliberate. */
export const LOCATION_KINDS = (t) => ([
  { key: 'warehouse', labelFa: t('operations.locationKinds.warehouse') },
  { key: 'room', labelFa: t('operations.locationKinds.room') },
  { key: 'fridge', labelFa: t('operations.locationKinds.fridge') },
  { key: 'cabinet', labelFa: t('operations.locationKinds.cabinet') },
  { key: 'custodian', labelFa: t('operations.locationKinds.custodian') },
]);

export const locationKindLabel = (key, t) =>
  LOCATION_KINDS(t).find((k) => k.key === key)?.labelFa || key || '—';

/** Document statuses, shared by requests, orders, issues, transfers and counts. */
export const DOC_STATUS_LABELS = (t) => ({
  draft: t('operations.docStatus.draft'),
  pending_approval: t('operations.docStatus.pendingApproval'),
  approved: t('common.state.approved'),
  rejected: t('operations.docStatus.rejected'),
  converted: t('operations.docStatus.converted'),
  ordered: t('operations.docStatus.ordered'),
  partially_received: t('operations.docStatus.partiallyReceived'),
  received: t('operations.docStatus.received'),
  cancelled: t('operations.docStatus.cancelled'),
  requested: t('operations.docStatus.requested'),
  issued: t('operations.docStatus.issued'),
  disputed: t('operations.docStatus.disputed'),
  shipped: t('operations.docStatus.shipped'),
  picked: t('operations.docStatus.picked'),
  open: t('operations.docStatus.open'),
  counting: t('operations.docStatus.counting'),
  review: t('operations.docStatus.review'),
  posted: t('operations.docStatus.posted'),
  abandoned: t('operations.docStatus.abandoned'),
  sent: t('operations.docStatus.sent'),
  credited: t('operations.docStatus.credited'),
  disposed: t('operations.docStatus.disposed'),
  blocked: t('operations.lotStates.blocked'),
  closed: t('operations.docStatus.closed'),
  inspected: t('operations.docStatus.inspected'),
  pending_inspection: t('operations.docStatus.pendingInspection'),
  short: t('operations.docStatus.short'),
  accepted: t('common.state.approved'),
  quarantine: t('operations.lotStates.quarantine'),
  damaged: t('operations.lotStates.damaged'),
  expired: t('operations.lotStates.expired'),
});

export const docStatusLabel = (key, t) => DOC_STATUS_LABELS(t)[key] || key || '—';

/** Which statuses still need somebody to act. Drives the rail badges. */
export const OPEN_DOC_STATUSES = {
  requests: ['draft', 'pending_approval'],
  orders: ['ordered', 'partially_received'],
  issues: ['requested', 'approved', 'issued', 'disputed'],
  counts: ['open', 'counting', 'review'],
};

/**
 * The stock figures worth showing side by side.
 *
 * Six, and never folded into one: a shelf holding 30 with 50 on order is not
 * short, and ordering again because the shelf says 30 is how a clinic ends up
 * with a year of gauze.
 */
export const POSITION_FIGURES = (t) => ([
  { key: 'onHand', labelFa: t('operations.position.onHand') },
  { key: 'available', labelFa: t('operations.position.available') },
  { key: 'reserved', labelFa: t('operations.position.reserved') },
  { key: 'inCustody', labelFa: t('operations.position.inCustody') },
  { key: 'inTransit', labelFa: t('operations.position.inTransit') },
  { key: 'onOrder', labelFa: t('operations.position.onOrder') },
]);

export const PERFORMER_ROLES = (t) => ([
  { key: 'doctor', labelFa: t('common.roles.doctor') },
  { key: 'nurse', labelFa: t('common.roles.nurse') },
  { key: 'physiotherapist', labelFa: t('common.roles.physiotherapist') },
  { key: 'technician', labelFa: t('common.roles.technician') },
  { key: 'radiologist', labelFa: t('common.roles.radiologist') },
]);

/**
 * «کاربرد» — what part of the mouth a service treats.
 *
 * A DENTAL-ONLY vocabulary, and the only field in the service form that is. It
 * exists because the dentist app's treatment plan attaches every line to a tooth
 * or deliberately to none, and until the catalogue could say which, that form
 * asked for a tooth on جرم‌گیری too — putting an invented tooth on a document a
 * patient signs. Getting it wrong costs a form field there and nothing else: the
 * plan line's own tooth stays the record of what was planned.
 *
 * `''` is a real option rather than a blank: «مشخص نشده» means the dentist
 * chooses, which is what every row did before this existed.
 *
 * The keys mirror ALLOWED_APPLICABILITIES in the clinicalencounter service.
 * Shared by the desktop drawer and the mobile sheet so the two cannot drift.
 */
export const APPLICABILITIES = (t) => ([
  { key: '', labelFa: t('operations.applicability.unset') },
  { key: 'tooth', labelFa: t('operations.applicability.tooth') },
  { key: 'surfaces', labelFa: t('operations.applicability.surfaces') },
  { key: 'quadrant', labelFa: t('operations.applicability.quadrant') },
  { key: 'arch', labelFa: t('operations.applicability.arch') },
  { key: 'mouth', labelFa: t('operations.applicability.mouth') },
]);

/**
 * «اثر روی چارت» — what a service DOES to the tooth it is performed on.
 *
 * The pair of APPLICABILITIES above: that one says WHERE a service happens,
 * this says what the tooth looks like afterwards. The dentist app's «ثبت انجام»
 * is the only reader — it PROPOSES this as the chart change, and the dentist
 * confirms, changes or clears it. Nothing here writes to any chart.
 *
 * It exists because a dental chart could learn nothing from work that touched
 * no SURFACE: a عصب‌کشی, a روکش and a کشیدن were recorded, billed, and left the
 * odontogram saying the tooth was untouched. The shared dictionary now answers
 * for its own 75 dental rows; without this field a service a CLINIC invented
 * would be the only kind that still says nothing.
 *
 * `''` and «تغییری در چارت ندهد» are different answers. The first leaves the
 * dentist's form to behave as it always has; the second is this clinic stating
 * that the service changes no tooth — worth saying out loud for a جرم‌گیری.
 *
 * The keys mirror ALLOWED_CHART_EFFECTS in clinicalencounter, whose words are
 * the CHART's own (patientmanagement's tooth events).
 */
export const CHART_EFFECTS = (t) => ([
  { key: '', labelFa: t('operations.applicability.unset') },
  { key: 'none', labelFa: t('operations.chartEffect.none') },
  { key: 'surface:restoration', labelFa: t('operations.chartEffect.surfaceRestoration') },
  { key: 'surface:sealant', labelFa: t('operations.chartEffect.surfaceSealant') },
  { key: 'surface:temporary', labelFa: t('operations.chartEffect.surfaceTemporary') },
  { key: 'condition:root_canal', labelFa: t('operations.chartEffect.rootCanal') },
  { key: 'condition:crown', labelFa: t('operations.chartEffect.crown') },
  { key: 'condition:veneer', labelFa: t('operations.chartEffect.veneer') },
  { key: 'condition:implant', labelFa: t('operations.chartEffect.implant') },
  { key: 'condition:post_core', labelFa: t('operations.chartEffect.postCore') },
  { key: 'condition:bridge_abutment', labelFa: t('operations.chartEffect.bridgeAbutment') },
  { key: 'condition:sealant', labelFa: t('operations.chartEffect.sealant') },
  { key: 'condition:temporary', labelFa: t('operations.chartEffect.temporary') },
  { key: 'presence:extracted', labelFa: t('operations.chartEffect.extracted') },
]);

export const COMMISSION_MODES = (t) => ([
  { key: 'percent', labelFa: t('operations.commission.percentOfTariff') },
  { key: 'fixed', labelFa: t('operations.commission.fixedAmount') },
]);

// ── The date window ──────────────────────────────────────────────────────────
// One window governs the whole module: the cost strip, the expense ledger, the
// purchase history. Frozen when the preset is picked, never computed in render —
// a live Date.now() in a render body makes the value fed to the hooks change every
// frame and refetches forever (the trap useCashier documents).

const DAY = 86_400_000;
// The clinic's UTC offset at an instant (PM-0088) — was a fixed +03:30.
const offsetAt = (ms) => zoneOffsetMs(ms);

export const DATE_PRESETS = (t) => ([
  { key: 'today', labelFa: t('common.dates.today') },
  { key: '7d', labelFa: t('common.ranges.last7Days') },
  { key: 'month', labelFa: t('common.ranges.thisMonth') },
  { key: 'all', labelFa: t('common.filters.all') },
]);

export const presetLabel = (key, t) =>
  DATE_PRESETS(t).find((p) => p.key === key)?.labelFa || '';

/** Epoch-ms window for a preset, on the clinic's clock. null = unbounded. */
export function presetRange(key, now = Date.now()) {
  if (key === 'all') return null;
  if (key === '7d') return { fromTs: now - 7 * DAY, toTs: now };

  const clinicMidnight = clinicDayStart(now);
  if (key === 'today') return { fromTs: clinicMidnight, toTs: clinicDayEnd(now) + 1 };

  // 'month' — the current JALALI month. A clinic's «این ماه» is مرداد, not August —
  // unless the brand keeps Gregorian books (lib/dateRange.js), where it is August.
  if (gregorianBooks()) return { fromTs: monthStartMs(now), toTs: now + DAY };
  const j = msToJalali(now + offsetAt(now));
  const monthStart = j
    ? wallToMs(new Date(jalaliToMs(j.y, j.m, 1)).toISOString().slice(0, 10), 0)
    : clinicMidnight;
  return { fromTs: monthStart, toTs: now + DAY };
}

/** «۲۶ تیر ۱۴۰۵» for an epoch-ms instant, in Iran local time. */
// `formatDate` and `formatTime` are threaded in — plain helpers, not
// components. The fixed-offset arithmetic these used to do by hand is what the
// formatters do from their descriptor's `timeZone`, so a ledger row reads
// 14:30 Tehran in every language without this file knowing about it.
export function fmtJalaliDay(ms, formatDate) {
  if (!ms) return '—';
  return formatDate(Number(ms), 'long');
}

/** «۲۶ تیر ۱۴۰۵ · ۱۴:۳۰» — the cardex needs the time, the ledger does not. */
export function fmtJalaliMoment(ms, formatDate, formatTime) {
  if (!ms) return '—';
  const n = Number(ms);
  return `${formatDate(n, 'long')} · ${formatTime(n)}`;
}

// ── Text ─────────────────────────────────────────────────────────────────────

/**
 * Some keyboard layouts (and pasted Arabic) produce ي/ك where Persian uses ی/ک.
 * Everything in this clinic's data is stored the Persian way, so a search typed the
 * Arabic way would otherwise match nothing. Fold both sides before comparing.
 */
export const normalizeFa = (s) =>
  // i18n-exempt: the CHARACTERS being folded, not copy. This is a search
  // normaliser over data the clinic stored in Persian, and it stays correct
  // whatever language the panel's chrome is in.
  String(s || '').replace(/ي/g, 'ی').replace(/ك/g, 'ک');

export const faIncludes = (haystack, needle) =>
  normalizeFa(haystack).includes(normalizeFa(needle));

// ── CSV ──────────────────────────────────────────────────────────────────────
//
// Re-exported from `src/lib/csv.js`, which is where the implementation now lives.
// It moved because a second, subtly different copy had appeared in the cash desk
// (different line ending, different digit handling), so the same «خروجی» button
// produced two dialects of file. The builders below still live here, because WHICH
// columns an expense export carries is this screen's business; how a CSV is spelled
// is not.
import { toCsv, downloadCsv, csvMoney } from '../../lib/csv';
import { costCenterLabel } from '../../lib/costCenter';
import { clinicDayEnd, clinicDayStart, wallToMs, zoneOffsetMs } from '../../lib/clinicZone';

export { toCsv, downloadCsv };


// Money cells are in each ROW's currency (its `currency`; blank is IRR), in the
// display unit the header names — toman for IRR, exactly as before. A clinic's rows
// share one currency, so a column never mixes two.
export function expensesCsv(expenses, t, formatDate) {
  return toCsv(
    [t('common.fields.date'), t('operations.fields.description'), t('operations.fields.payee'), t('operations.fields.category'), t('operations.fields.costCentre'), t('operations.fields.amountToman'), t('common.fields.status'), t('common.actions.confirm')],
    expenses.map((e) => [
      fmtJalaliDay(e.occurredAt, formatDate),
      // i18n-exempt: CSV-hostile characters stripped from a description — the
      // Persian semicolon «؛» is one of them. Punctuation, not copy.
      (e.description || '').replace(/[\r\n,؛]/g, ' '),
      e.payee || '',
      categoryLabel(e.category, t),
      costCenterLabel(e.costCenter, t),
      String(toDisplay(e.amountRial, e.currency) ?? 0),
      e.status === 'void' ? t('accounting.status.void') : t('common.state.active'),
      approvalLabel(e.approvalStatus, t),
    ]),
  );
}

export function itemsCsv(items, t) {
  return toCsv(
    [t('operations.fields.itemName'), t('operations.fields.code'), t('operations.fields.category'), t('operations.fields.unit'), t('operations.fields.stock'), t('operations.fields.minimum'), t('operations.fields.costPriceToman'), t('operations.fields.sellingPriceToman'), t('operations.fields.stockValueToman'), t('common.fields.status')],
    items.map((i) => [
      i.name, i.code, itemCategoryLabel(i.category, t), i.unit,
      String(i.currentStock ?? 0), String(i.minimumStock ?? 0),
      // TOMAN under the «(تومان)» headers. These three wrote RIALS until the
      // currency work — ten times the money under a toman label.
      csvMoney(i.averageCost || 0, i.currency),
      csvMoney(i.sellingPrice || 0, i.currency),
      csvMoney((i.averageCost || 0) * Math.max(i.currentStock || 0, 0), i.currency),
      stockState(i, t).labelFa,
    ]),
  );
}


// ── قبوض — bills (docs/CLINIC_BILL_MANAGEMENT_DESIGN.md) ─────────────────────
//
// Mirrors the vocabularies in the accounting service's app/domain/bills.py. Same
// rule as the expense taxonomy above: `tone` names a semantic role, never a colour.

export const BILL_KINDS = (t) => ([
  { key: 'electricity', labelFa: t('operations.billKinds.electricity') },
  { key: 'water', labelFa: t('operations.billKinds.water') },
  { key: 'gas', labelFa: t('operations.billKinds.gas') },
  { key: 'landline', labelFa: t('operations.billKinds.landline') },
  { key: 'mobile', labelFa: t('operations.billKinds.mobile') },
  { key: 'internet', labelFa: t('operations.billKinds.internet') },
  { key: 'municipality', labelFa: t('operations.billKinds.municipality') },
  { key: 'tax', labelFa: t('operations.billKinds.tax') },
  { key: 'insurance_premium', labelFa: t('operations.billKinds.insurancePremium') },
  { key: 'rent', labelFa: t('operations.billKinds.rent') },
  { key: 'service_charge', labelFa: t('operations.billKinds.serviceCharge') },
  { key: 'other', labelFa: t('common.state.other') },
]);

export const billKindLabel = (key, t) =>
  BILL_KINDS(t).find((k) => k.key === key)?.labelFa || key || '—';

export const BILL_CADENCES = (t) => ([
  { key: 'monthly', labelFa: t('operations.billCadences.monthly') },
  { key: 'bimonthly', labelFa: t('operations.billCadences.bimonthly') },
  { key: 'quarterly', labelFa: t('operations.billCadences.quarterly') },
  { key: 'yearly', labelFa: t('operations.billCadences.yearly') },
  { key: 'custom', labelFa: t('operations.billCadences.custom') },
]);

export const billCadenceLabel = (key, t) =>
  BILL_CADENCES(t).find((c) => c.key === key)?.labelFa || key || '—';

/** How many Jalali months one period spans. `custom` has no month span. */
export const CADENCE_MONTHS = { monthly: 1, bimonthly: 2, quarterly: 3, yearly: 12 };

/**
 * The four states a person picks from. «سررسید گذشته» is NOT here: it is derived
 * by the service (`isOverdue`) from the due date, so it can never be stale and can
 * never be SET — which is why the form renders it and does not offer it.
 */
export const BILL_STATUSES = (t) => ([
  { key: 'expected', labelFa: t('operations.billStatuses.expected'), tone: 'accent' },
  { key: 'unpaid', labelFa: t('operations.billStatuses.unpaid'), tone: 'mute' },
  { key: 'paid', labelFa: t('operations.billStatuses.paid'), tone: 'good' },
  { key: 'cancelled', labelFa: t('operations.billStatuses.cancelled'), tone: 'mute' },
]);

/**
 * What a row should SAY, and in what tone — derived flags first.
 *
 * A bill that is unpaid and past its due date is «سررسید گذشته», not «پرداخت نشده»:
 * the stored status is the same for both, and showing it would bury the only rows
 * that need acting on today.
 */
export function billStatusView(bill, t) {
  if (!bill) return { labelFa: '—', tone: 'mute' };
  if (bill.status === 'unpaid' && bill.isOverdue) {
    return { labelFa: t('operations.billStatuses.overdue'), tone: 'danger' };
  }
  if (bill.status === 'unpaid' && bill.isDueSoon) {
    return { labelFa: t('operations.billStatuses.dueSoon'), tone: 'warn' };
  }
  if (bill.status === 'unpaid' && Number(bill.settledRial) > 0) {
    return { labelFa: t('operations.billStatuses.partiallyPaid'), tone: 'warn' };
  }
  const s = BILL_STATUSES(t).find((x) => x.key === bill.status);
  return { labelFa: s?.labelFa || bill.status || '—', tone: s?.tone || 'mute' };
}

/** What is still owed on a bill, late fee included. Never negative on screen. */
export const billOutstandingRial = (bill) => Math.max(
  0,
  (Number(bill?.amountRial) || 0) + (Number(bill?.lateFeeRial) || 0)
    - (Number(bill?.settledRial) || 0),
);

/**
 * The requirement's «روش پرداخت» list, in its order.
 *
 * `direct_debit` is «حساب بانکی» — a standing order the BANK executes. Picking it
 * as a type's default also silences that type's due-soon warning in the service:
 * a bill that pays itself must never ask anyone to act.
 */
export const BILL_PAYMENT_METHODS = (t) => ([
  { key: 'card', labelFa: t('operations.billMethods.card') },
  { key: 'bank_transfer', labelFa: t('operations.billMethods.bankTransfer') },
  { key: 'direct_debit', labelFa: t('operations.billMethods.directDebit') },
  { key: 'cash', labelFa: t('operations.billMethods.cash') },
  { key: 'other', labelFa: t('common.state.other') },
]);

export const billMethodLabel = (key, t) =>
  BILL_PAYMENT_METHODS(t).find((m) => m.key === key)?.labelFa || key || '—';

/** Methods the clinic cannot reconstruct from its own drawer need a «شماره پیگیری». */
export const BILL_REFERENCE_REQUIRED = new Set(['card', 'bank_transfer', 'direct_debit']);
export const billNeedsReference = (method) => BILL_REFERENCE_REQUIRED.has(method);

/**
 * A second bill inside one period. The `regular` row is the period's own; the rest
 * exist so a correction after payment is a NEW signed row rather than an edit to a
 * paid one.
 */
export const BILL_SUB_KINDS = (t) => ([
  { key: 'regular', labelFa: t('operations.billSubKinds.regular') },
  { key: 'interim', labelFa: t('operations.billSubKinds.interim') },
  { key: 'correction', labelFa: t('operations.billSubKinds.correction') },
  { key: 'penalty', labelFa: t('operations.billSubKinds.penalty') },
]);

export const billSubKindLabel = (key, t) =>
  BILL_SUB_KINDS(t).find((s) => s.key === key)?.labelFa || '';

/** Kinds with no شناسه قبض to type — the fields are hidden rather than optional. */
const KINDS_WITHOUT_IDENTIFIERS = new Set(['rent', 'service_charge', 'insurance_premium', 'other']);
export const kindWantsIdentifiers = (kind) => !KINDS_WITHOUT_IDENTIFIERS.has(kind);

/**
 * The Iran-only rails — Iranian شناسه قبض / شناسه پرداخت (which encode the amount
 * in thousands of RIALS), the Finnotech inquiry and Sepehr online payment — exist
 * only for a clinic that keeps its books in IRR (docs/clinic-currency/CONTRACTS.md
 * §6). Anywhere else they are HIDDEN, not disabled: accounting refuses every one of
 * them with RAIL_REQUIRES_IRR, and a greyed-out control would only invite the
 * question. Blank is IRR.
 *
 * And only in the Iranian market: a brand that says `market: 'intl'`
 * (docs/rebranding/CONTRACT.md) has no Iranian bills, inquiry or bank gateway,
 * whatever a clinic's currency says.
 */
export const iranRailsAvailable = (currency) => isIranMarket() && (!currency || currency === 'IRR');

/** Whether a bill can still be edited. Paid and cancelled rows are history. */
export const billIsEditable = (bill) =>
  !!bill && bill.status !== 'paid' && bill.status !== 'cancelled';

/** Whether a payment may be recorded — the drawer's primary action depends on it. */
export const billIsPayable = (bill, billType) => {
  if (!bill || bill.status !== 'unpaid') return false;
  if (billType?.requiresAuthorization && !bill.authorizedAt) return false;
  return true;
};

/** A generated placeholder waiting for the real bill to arrive. */
export const billIsPlaceholder = (bill) => bill?.status === 'expected';

// ── Jalali periods (mirrors app/domain/bills.py) ─────────────────────────────

/** «1405-06» for an epoch-ms instant, on the clinic's clock. */
export function periodKeyOf(ms = Date.now()) {
  const j = msToJalali(ms + offsetAt(ms));
  return j ? `${String(j.y).padStart(4, '0')}-${String(j.m).padStart(2, '0')}` : '';
}

export function parsePeriodKey(key) {
  const [y, m] = String(key || '').split('-');
  const jy = Number(y);
  const jm = Number(m);
  return Number.isFinite(jy) && Number.isFinite(jm) && jm >= 1 && jm <= 12 ? { jy, jm } : null;
}

export function addJalaliMonths(jy, jm, n) {
  const total = jy * 12 + (jm - 1) + n;
  return { jy: Math.floor(total / 12), jm: (total % 12) + 1 };
}

/**
 * «شهریور ۱۴۰۵» for one month, «مرداد–شهریور ۱۴۰۵» for a span.
 *
 * The service sends `periodLabel` on every bill and THAT is what a row renders;
 * this exists for the period PICKER, which has to label months that have no bill
 * yet.
 *
 * `monthNames` is the caller's `clinicMonthNames()` — the clinic's calendar in the
 * READER's language, «مرداد» or «Mordad» but never «August». Threading it in is
 * what keeps a hardcoded Persian table out of an otherwise-English form; digits
 * stay Latin here and the caller runs them through `formatDigits`.
 */
export function periodLabelOf(key, months = 1, monthNames = []) {
  const p = parsePeriodKey(key);
  if (!p) return key || '—';
  const name = (m) => monthNames[m - 1] || String(m);
  if (months <= 1) return `${name(p.jm)} ${p.jy}`;
  const end = addJalaliMonths(p.jy, p.jm, months - 1);
  return end.jy === p.jy
    ? `${name(p.jm)}–${name(end.jm)} ${p.jy}`
    : `${name(p.jm)} ${p.jy}–${name(end.jm)} ${end.jy}`;
}

/**
 * The periods a bill could be raised for: the current cycle and the ones before it,
 * newest first. Offered as a picker rather than a free-text field because a typed
 * «1405-6» that misses its zero pad sorts wrongly forever.
 */
export function recentPeriods(cadence = 'monthly', count = 14, now = Date.now()) {
  const months = CADENCE_MONTHS[cadence] || 1;
  const cur = parsePeriodKey(periodKeyOf(now));
  if (!cur) return [];
  const out = [];
  // One period AHEAD as well: a bill for next month can legitimately arrive early.
  let { jy, jm } = addJalaliMonths(cur.jy, cur.jm, months);
  for (let i = 0; i < count; i += 1) {
    out.push(`${String(jy).padStart(4, '0')}-${String(jm).padStart(2, '0')}`);
    ({ jy, jm } = addJalaliMonths(jy, jm, -months));
  }
  return out;
}

// ── CSV ──────────────────────────────────────────────────────────────────────

export function billsCsv(bills, t, formatDate) {
  return toCsv(
    [
      t('common.fields.status'), t('operations.fields.billType'), t('operations.fields.period'),
      t('operations.fields.amountToman'), t('operations.fields.lateFeeToman'),
      t('operations.fields.paidToman'), t('operations.fields.dueDate'),
      t('operations.fields.billIdentifier'), t('operations.fields.paymentIdentifier'),
    ],
    bills.map((b) => [
      billStatusView(b, t).labelFa,
      b.billTypeTitle || '',
      b.periodLabel || b.periodKey || '',
      String(toDisplay(b.amountRial, b.currency) ?? 0),
      String(toDisplay(b.lateFeeRial, b.currency) ?? 0),
      String(toDisplay(b.settledRial, b.currency) ?? 0),
      b.dueAt ? fmtJalaliDay(b.dueAt, formatDate) : '',
      // Quoted as text by toCsv, so Excel cannot turn a 13-digit identifier into
      // 1.23457E+12 — which is exactly what it does to a bare long number.
      b.billIdentifier || '',
      b.paymentIdentifier || '',
    ]),
  );
}

// ── Derivations the cost strip and the tables share ──────────────────────────

/**
 * «مدیریت هزینه‌ها»'s headline: everything money-OUT for the window, plus what
 * the clinic still owes.
 *
 * Deliberately assembled from what each source already returns rather than
 * re-summed here: `summary` counts pending + approved and drops rejected, and
 * `billsDashboard` derives «سررسید گذشته» from the stored due date against
 * today. Re-deriving either rule in the client is how the KPI and the list it
 * sits above start disagreeing about the same month.
 *
 * A source that has not arrived yields `null`, never `0`. A zero is a claim
 * about the business — «این ماه هیچ هزینه‌ای نداشتیم» — and an outage must not
 * be allowed to make it (CLAUDE.md, clinic reports: «a figure that does not
 * exist is null, never 0, at every layer»).
 */
export function expenseStrip({ summary, billsDashboard }) {
  const byCategory = Object.fromEntries(
    (summary?.byCategory || []).map((c) => [c.category, Number(c.totalRial) || 0]),
  );
  const hasSummary = !!summary;
  const hasBills = !!billsDashboard;
  return {
    totalRial: hasSummary ? Number(summary.totalRial) || 0 : null,
    // Goods receipts post themselves as consumable expenses, so this is a SLICE of
    // totalRial, not something to add to it. It is also the one figure on this
    // page that belongs to the other one, which is why its card links there.
    purchasesRial: hasSummary ? byCategory.consumable || 0 : null,
    personnelRial: hasSummary ? byCategory.personnel || 0 : null,
    pendingRial: hasSummary ? Number(summary.pendingRial) || 0 : null,
    pendingCount: hasSummary ? Number(summary.pendingCount) || 0 : null,
    // What the clinic owes and has not paid. It replaced «ارزش انبار» when the
    // warehouse left for «کالاها و خدمات»: the page already computed it for the
    // rail badge, and it is the number a manager looks for first.
    overdueCount: hasBills ? Number(billsDashboard.overdueCount) || 0 : null,
    overdueRial: hasBills ? Number(billsDashboard.overdueRial) || 0 : null,
    dueSoonCount: hasBills ? Number(billsDashboard.dueSoonCount) || 0 : null,
  };
}

/**
 * The declarations «خدمات» lists and edits: the clinic's own, one per service.
 *
 * A declaration with a `doctorId` is not a second service. It is one
 * practitioner's OWN price for a clinic service (their visit at their price),
 * written and edited in «همکاران» (docs/CLINIC_PRACTITIONER_ENGAGEMENT_DESIGN.md).
 * Listed here it was an unlabelled duplicate — «ویزیت» twice, at two prices —
 * and saving it through the service form, which never names a doctor, landed on
 * the CLINIC-WIDE row: its price, its performers and its whole cost model,
 * every colleague's personal share included.
 */
export const clinicWideServices = (services) => (services || []).filter((s) => !s.doctorId);

/**
 * The declaration a `?service=<id>` link opens, or null.
 *
 * «همکاران» links to a service's clinic-wide definition, but for a practitioner's
 * own price it hands over the id of THAT row. The form edits the clinic's
 * definition, so the link resolves to the clinic-wide row of the same catalogue
 * entry — and to nothing when the clinic has not declared one.
 */
export function serviceToOpen(services, id) {
  const list = services || [];
  const found = list.find((s) => s.id === id);
  if (!found?.doctorId) return found || null;
  return list.find((s) => !s.doctorId && s.catalogId === found.catalogId) || null;
}

/**
 * «کالاها و خدمات»'s figures: what is on the shelf, what needs attention, and
 * how much of the clinic's offer is actually billable. Since PM-0495 the page
 * shows none of them as a strip; the rail badges still read the stock counts.
 *
 * Same null discipline as `expenseStrip`, and the same rule about not
 * re-deriving: `lowStockCount` comes from the BFF (which counts `<=` the
 * minimum, exactly as `stockState` does) rather than being recounted over the
 * page of items that happens to be loaded.
 *
 * `services` is the DECLARED list, so «بدون تعرفه» is counted here and not on
 * the server: a declaration priced at nothing is still a declaration, and the
 * count is what tells a clinic its cash desk cannot bill half its own offer.
 *
 * It counts EVERY declaration without a price, active or not. An unpriced
 * service is almost always inactive — «ثبت بعداً» saves it switched off, and the
 * drawer will not save a price of zero — so counting only the active ones kept
 * the card at «همه دارای تعرفه» while the list below carried «بدون تعرفه» rows.
 */
export function goodsStrip({ inventorySummary, equipmentAlerts, services }) {
  const hasInventory = !!inventorySummary;
  const declared = Array.isArray(services) ? services : null;
  const active = declared?.filter((s) => s.isActive !== false) || null;
  const unpriced = declared?.filter((s) => !(Number(s.unitPrice) > 0)) || null;
  return {
    stockValueRial: hasInventory ? Number(inventorySummary.totalValue) || 0 : null,
    lowStockCount: hasInventory ? Number(inventorySummary.lowStockCount) || 0 : null,
    outOfStockCount: hasInventory ? Number(inventorySummary.outOfStockCount) || 0 : null,
    // `null` when the register was never read — which is the case for anybody
    // without `equipment.view`, and is why the card is absent rather than zero.
    equipmentAlertCount: Array.isArray(equipmentAlerts)
      ? equipmentAlertCounts(equipmentAlerts).total
      : null,
    servicesActiveCount: active ? active.length : null,
    servicesUnpricedCount: unpriced ? unpriced.length : null,
  };
}

/**
 * «کالاها و خدمات»'s way to its numbers: the report that answers for the view
 * the person is on, or null when they may not open it (PM-0495).
 *
 * The page carries no stat strip — figures about the shelf, the offer and the
 * devices are a subject of their own and live in «گزارش‌ها» (DESIGN_CONCEPT §2.3.1).
 * The link is the report's own permission, read from the report registry, so a
 * person never gets a link that lands on the overview instead.
 */
const GOODS_REPORTS = {
  items: 'inventory-value', expiry: 'inventory-value', procurement: 'inventory-value',
  custody: 'inventory-value', counts: 'inventory-value', locations: 'inventory-value',
  suppliers: 'inventory-value',
  services: 'services',
  equipment: 'equipment', equipmentTypes: 'equipment',
  insurance: 'insurance',
};
const GOODS_REPORT_LABEL = {
  'inventory-value': 'goods.reports.inventory',
  services: 'goods.reports.services',
  equipment: 'goods.reports.equipment',
  insurance: 'goods.reports.insurance',
};
export function goodsReportFor(view, can) {
  const report = GOODS_REPORTS[view];
  const perm = report ? reportPermFor(report) : null;
  if (!perm) return null;
  const allowed = Array.isArray(perm) ? perm.every((p) => can(p)) : can(perm);
  return allowed ? { view: report, labelKey: GOODS_REPORT_LABEL[report] } : null;
}

// `money` used to live here as a re-export of the framework's Persian-only
// `fmtToman`. Every call site now uses `formatMoney` from `useTranslation()`
// directly — same rial→toman conversion, but grouped and digit-set in the
// reader's own locale.


// ── تجهیزات — the equipment register (docs/CLINIC_EQUIPMENT_MANAGEMENT_DESIGN.md) ──
//
// Every set below mirrors one in the warehousing service's app/domain/equipment.py.
// The service VALIDATES against its copy; a value here it does not know is a hard
// error at save time, never a cosmetic mismatch. The status machine itself is ONE
// JSON, byte-identical in both repositories (`dev/check-equipment-transitions.sh`),
// and this side reads it only to grey buttons — the service decides.

export const EQUIPMENT_MACHINE = equipmentTransitions;

/** The kinds legal from a status, in fixture order — what the drawer offers. */
export function equipmentKindsFrom(status) {
  return Object.entries(EQUIPMENT_MACHINE.kinds)
    .filter(([, spec]) => !spec.system && spec.from.includes(status))
    .map(([kind]) => kind);
}

/** Which door an event kind goes through — the permission it needs. */
export function equipmentKindPermission(kind) {
  const spec = EQUIPMENT_MACHINE.kinds[kind] || {};
  if (spec.retire) return 'equipment.retire';
  if (spec.service) return 'equipment.service';
  return 'equipment.manage';
}

/** Kinds that may carry money — the amount field is shown only for these. */
export const equipmentKindHasMoney = (kind) => !!EQUIPMENT_MACHINE.kinds[kind]?.money;

export const EQUIPMENT_STATUSES = (t) => ([
  { key: 'in_service', labelFa: t('operations.equipment.status.inService'), tone: 'good' },
  { key: 'under_repair', labelFa: t('operations.equipment.status.underRepair'), tone: 'danger' },
  { key: 'idle', labelFa: t('operations.equipment.status.idle'), tone: 'mute' },
  { key: 'ordered', labelFa: t('operations.equipment.status.ordered'), tone: 'info' },
  { key: 'received', labelFa: t('operations.equipment.status.received'), tone: 'info' },
  { key: 'installed', labelFa: t('operations.equipment.status.installed'), tone: 'info' },
  { key: 'retired', labelFa: t('operations.equipment.status.retired'), tone: 'mute' },
  { key: 'sold', labelFa: t('operations.equipment.status.sold'), tone: 'mute' },
  { key: 'scrapped', labelFa: t('operations.equipment.status.scrapped'), tone: 'mute' },
]);

export const equipmentStatusLabel = (key, t) =>
  EQUIPMENT_STATUSES(t).find((s) => s.key === key)?.labelFa || key || '—';

export const equipmentStatusTone = (key, t) =>
  EQUIPMENT_STATUSES(t).find((s) => s.key === key)?.tone || 'mute';

/** The list's status chips: the three states a person works, plus «خارج از چرخه» for all three ends. */
export const EQUIPMENT_STATUS_FILTERS = (t) => ([
  { key: 'all', labelFa: t('common.filters.all'), statuses: null },
  { key: 'active', labelFa: t('operations.equipment.filters.active'), statuses: ['ordered', 'received', 'installed', 'in_service', 'idle', 'under_repair'] },
  { key: 'in_service', labelFa: t('operations.equipment.status.inService'), statuses: ['in_service'] },
  { key: 'under_repair', labelFa: t('operations.equipment.status.underRepair'), statuses: ['under_repair'] },
  { key: 'idle', labelFa: t('operations.equipment.status.idle'), statuses: ['idle'] },
  { key: 'out', labelFa: t('operations.equipment.status.retired'), statuses: ['retired', 'sold', 'scrapped'] },
]);

export const EQUIPMENT_KINDS = (t) => ({
  purchased: t('operations.equipment.kinds.purchased'),
  received: t('operations.equipment.kinds.received'),
  installed: t('operations.equipment.kinds.installed'),
  commissioned: t('operations.equipment.kinds.commissioned'),
  serviced: t('operations.equipment.kinds.serviced'),
  calibrated: t('operations.equipment.kinds.calibrated'),
  checked: t('operations.equipment.kinds.checked'),
  meter_read: t('operations.equipment.kinds.meterRead'),
  fault_reported: t('operations.equipment.kinds.faultReported'),
  repair_started: t('operations.equipment.kinds.repairStarted'),
  repaired: t('operations.equipment.kinds.repaired'),
  idle: t('operations.equipment.kinds.idle'),
  reactivated: t('operations.equipment.kinds.reactivated'),
  moved: t('operations.equipment.kinds.moved'),
  retired: t('operations.equipment.kinds.retired'),
  sold: t('operations.equipment.kinds.sold'),
  scrapped: t('operations.equipment.kinds.scrapped'),
  depreciated: t('operations.equipment.kinds.depreciated'),
});

export const equipmentKindLabel = (kind, t) => EQUIPMENT_KINDS(t)[kind] || kind || '—';

/** The tone a history row wears. A fault is danger, money-in is good, the rest by role. */
export const equipmentKindTone = (kind) => ({
  fault_reported: 'danger', repaired: 'good', serviced: 'accent', calibrated: 'info',
  checked: 'mute', commissioned: 'good', reactivated: 'good', retired: 'mute',
  sold: 'good', scrapped: 'mute', depreciated: 'mute', purchased: 'info',
  installed: 'info', received: 'info', moved: 'mute', idle: 'mute', meter_read: 'mute',
  repair_started: 'warn',
}[kind] || 'mute');

export const EQUIPMENT_OWNERSHIPS = (t) => ([
  { key: 'owned', labelFa: t('operations.equipment.ownership.owned') },
  { key: 'rented', labelFa: t('operations.equipment.ownership.rented') },
  { key: 'loaned', labelFa: t('operations.equipment.ownership.loaned') },
]);

export const EQUIPMENT_CRITICALITIES = (t) => ([
  { key: 'critical', labelFa: t('operations.equipment.criticality.critical'), tone: 'danger' },
  { key: 'high', labelFa: t('operations.equipment.criticality.high'), tone: 'warn' },
  { key: 'medium', labelFa: t('operations.equipment.criticality.medium'), tone: 'info' },
  { key: 'low', labelFa: t('operations.equipment.criticality.low'), tone: 'mute' },
]);

export const criticalityLabel = (key, t) =>
  EQUIPMENT_CRITICALITIES(t).find((c) => c.key === key)?.labelFa || key || '—';

export const criticalityTone = (key, t) =>
  EQUIPMENT_CRITICALITIES(t).find((c) => c.key === key)?.tone || 'mute';

export const EQUIPMENT_PLAN_KINDS = (t) => ([
  { key: 'periodic', labelFa: t('operations.equipment.planKinds.periodic') },
  { key: 'calibration', labelFa: t('operations.equipment.planKinds.calibration') },
]);

export const EQUIPMENT_TRIGGERS = (t) => ([
  { key: 'calendar', labelFa: t('operations.equipment.triggers.calendar') },
  { key: 'usage', labelFa: t('operations.equipment.triggers.usage') },
  { key: 'hours', labelFa: t('operations.equipment.triggers.hours') },
  { key: 'whichever_first', labelFa: t('operations.equipment.triggers.whicheverFirst') },
]);

export const triggerLabel = (key, t) =>
  EQUIPMENT_TRIGGERS(t).find((x) => x.key === key)?.labelFa || key || '—';

export const EQUIPMENT_CONTRACT_KINDS = (t) => ([
  { key: 'warranty', labelFa: t('operations.equipment.contractKinds.warranty') },
  { key: 'service_contract', labelFa: t('operations.equipment.contractKinds.serviceContract') },
]);

export const contractKindLabel = (key, t) =>
  EQUIPMENT_CONTRACT_KINDS(t).find((x) => x.key === key)?.labelFa || key || '—';

export const EQUIPMENT_COVERAGES = (t) => ([
  { key: 'full', labelFa: t('operations.equipment.coverage.full') },
  { key: 'parts', labelFa: t('operations.equipment.coverage.parts') },
  { key: 'labor', labelFa: t('operations.equipment.coverage.labor') },
]);

export const EQUIPMENT_DEPRECIATION_METHODS = (t) => ([
  { key: 'straight_line', labelFa: t('operations.equipment.depreciation.straightLine') },
  { key: 'per_use', labelFa: t('operations.equipment.depreciation.perUse') },
]);

export const CHECK_RESULTS = (t) => ([
  { key: 'pass', labelFa: t('operations.equipment.check.pass') },
  { key: 'fail', labelFa: t('operations.equipment.check.fail') },
]);

/**
 * The statutory presets («جدول استهلاکات») a clinic picks a type from, so its
 * first action is «انتخاب» rather than «تایپ». Lives here, not in the service:
 * the numbers are defaults the clinic may override per type or per device, and
 * a Persian table in a Python module would be a second copy of copy.
 *
 * `usefulLifeMonths` is the straight-line default; a device the clinic bought
 * second-hand or runs harder overrides it on the device.
 *
 * `clinicTypes` is which «نوع کلینیک» the device belongs to — keys of the
 * clinicmanagement registry (`useClinicTypes`). An eye clinic opening this list
 * wants a slit lamp, not a dental unit; `equipmentPresetsFor` narrows by it.
 */
const PRIMARY_CARE = ['general', 'specialty', 'internal', 'pediatric', 'cardiology'];
const PROCEDURAL = [
  'general', 'specialty', 'dental', 'ophthalmology', 'dermatology', 'pediatric', 'cardiology',
  'orthopedic', 'neurology', 'gynecology', 'internal', 'ent', 'urology', 'aesthetic',
];
const REHAB = ['physiotherapy', 'rehabilitation', 'orthopedic'];

const P = (key, code, i18nKey, usefulLifeMonths, serviceIntervalDays, calibrationIntervalDays, criticality, clinicTypes) => ({
  key, code, i18nKey, usefulLifeMonths, serviceIntervalDays,
  needsCalibration: !!calibrationIntervalDays, calibrationIntervalDays: calibrationIntervalDays || null,
  criticality, clinicTypes,
});

const PRESET_TABLE = [
  // Sterilisation and utilities
  P('autoclave', 'AUTOCLAVE', 'autoclave', 120, 180, 365, 'critical', [...PROCEDURAL, 'laboratory']),
  P('sterilizer_dry', 'DRY-HEAT', 'dryHeat', 120, 365, 365, 'critical', ['general', 'specialty', 'dental', 'dermatology', 'gynecology', 'ent', 'aesthetic']),
  P('compressor', 'COMPRESSOR', 'compressor', 96, 180, null, 'medium', ['dental']),
  P('suction', 'SUCTION', 'suction', 96, 365, null, 'medium', ['general', 'specialty', 'dental', 'pediatric', 'internal', 'ent', 'gynecology', 'urology']),
  P('electrocautery', 'CAUTERY', 'electrocautery', 96, 365, 365, 'high', ['general', 'specialty', 'dermatology', 'aesthetic', 'gynecology', 'ent', 'urology']),
  P('cold_storage', 'MED-FRIDGE', 'coldStorage', 96, 365, 365, 'high', ['general', 'specialty', 'pediatric', 'internal', 'pharmacy', 'laboratory']),
  // Monitoring and emergency
  P('ecg', 'ECG', 'ecg', 96, 365, 365, 'high', PRIMARY_CARE),
  P('vital_monitor', 'MONITOR', 'vitalMonitor', 96, 365, 365, 'high', [...PRIMARY_CARE, 'aesthetic']),
  P('defibrillator', 'AED', 'defibrillator', 96, 180, 365, 'critical', ['general', 'specialty', 'cardiology', 'internal', 'aesthetic']),
  P('oxygen', 'OXYGEN', 'oxygen', 96, 365, null, 'high', ['general', 'specialty', 'internal', 'pediatric', 'cardiology', 'dental']),
  P('nebulizer', 'NEBULIZER', 'nebulizer', 60, 365, null, 'medium', ['general', 'specialty', 'pediatric', 'internal', 'ent']),
  // Dental
  P('dental_unit', 'DENTAL-UNIT', 'dentalUnit', 120, 180, null, 'high', ['dental']),
  P('dental_xray', 'DENTAL-XRAY', 'dentalXray', 120, 365, 365, 'critical', ['dental']),
  P('light_cure', 'LIGHT-CURE', 'lightCure', 60, 365, 365, 'medium', ['dental']),
  P('scaler', 'SCALER', 'scaler', 60, 365, null, 'medium', ['dental']),
  // Eye
  P('slit_lamp', 'SLIT-LAMP', 'slitLamp', 120, 365, null, 'high', ['ophthalmology']),
  P('autorefractometer', 'AUTOREF', 'autorefractometer', 96, 365, 365, 'high', ['ophthalmology']),
  P('tonometer', 'TONOMETER', 'tonometer', 96, 365, 365, 'high', ['ophthalmology']),
  P('oct', 'OCT', 'oct', 96, 365, 365, 'high', ['ophthalmology']),
  // Skin and aesthetic
  P('laser', 'LASER', 'laser', 60, 180, 365, 'high', ['dermatology', 'aesthetic', 'ophthalmology', 'dental']),
  P('dermatoscope', 'DERMATOSCOPE', 'dermatoscope', 60, 365, null, 'medium', ['dermatology', 'aesthetic']),
  P('cryotherapy', 'CRYO', 'cryotherapy', 96, 365, null, 'medium', ['dermatology', 'aesthetic']),
  // Heart
  P('echocardiography', 'ECHO', 'echocardiography', 96, 365, 365, 'high', ['cardiology']),
  P('holter', 'HOLTER', 'holter', 60, 365, 365, 'medium', ['cardiology', 'internal']),
  P('stress_test', 'TREADMILL', 'stressTest', 96, 180, 365, 'high', ['cardiology']),
  // Nerves and mind
  P('eeg', 'EEG', 'eeg', 96, 365, 365, 'high', ['neurology', 'psychology']),
  P('emg', 'EMG', 'emg', 96, 365, 365, 'high', ['neurology', 'rehabilitation']),
  P('neurofeedback', 'NEUROFEEDBACK', 'neurofeedback', 60, 365, null, 'medium', ['psychology', 'neurology']),
  // Women, ENT, urology
  P('ultrasound', 'ULTRASOUND', 'ultrasound', 96, 365, null, 'medium', ['general', 'specialty', 'gynecology', 'internal', 'urology', 'radiology']),
  P('colposcope', 'COLPOSCOPE', 'colposcope', 96, 365, null, 'medium', ['gynecology']),
  P('fetal_doppler', 'FETAL-DOPPLER', 'fetalDoppler', 60, 365, 365, 'medium', ['gynecology']),
  P('endoscope', 'ENDOSCOPE', 'endoscope', 60, 180, null, 'high', ['ent', 'urology', 'internal']),
  P('audiometer', 'AUDIOMETER', 'audiometer', 96, 365, 365, 'high', ['ent']),
  P('uroflowmeter', 'UROFLOW', 'uroflowmeter', 96, 365, 365, 'medium', ['urology']),
  // Physiotherapy and rehabilitation
  P('electrotherapy', 'ELECTROTHERAPY', 'electrotherapy', 96, 365, 365, 'medium', REHAB),
  P('therapeutic_ultrasound', 'US-THERAPY', 'therapeuticUltrasound', 96, 365, 365, 'medium', REHAB),
  P('traction', 'TRACTION', 'traction', 120, 365, null, 'medium', REHAB),
  P('shockwave', 'SHOCKWAVE', 'shockwave', 96, 365, 365, 'medium', REHAB),
  // Imaging
  P('imaging', 'IMAGING', 'imaging', 120, 180, 365, 'critical', ['radiology', 'orthopedic']),
  P('xray', 'XRAY', 'xray', 120, 180, 365, 'critical', ['radiology', 'orthopedic']),
  P('mammography', 'MAMMO', 'mammography', 120, 180, 365, 'critical', ['radiology']),
  P('ct', 'CT', 'ct', 120, 90, 365, 'critical', ['radiology']),
  P('mri', 'MRI', 'mri', 120, 90, 365, 'critical', ['radiology']),
  // Laboratory
  P('centrifuge', 'CENTRIFUGE', 'centrifuge', 96, 365, 365, 'high', ['laboratory']),
  P('cell_counter', 'CBC', 'cellCounter', 96, 180, 180, 'critical', ['laboratory']),
  P('chemistry_analyzer', 'CHEM', 'chemistryAnalyzer', 96, 180, 180, 'critical', ['laboratory']),
  P('microscope', 'MICROSCOPE', 'microscope', 120, 365, null, 'medium', ['laboratory', 'dermatology']),
  P('incubator', 'INCUBATOR', 'incubator', 96, 365, 365, 'high', ['laboratory']),
];

export const EQUIPMENT_TYPE_PRESETS = (t) => PRESET_TABLE.map(({ i18nKey, ...p }) => ({
  ...p, labelFa: t(`operations.equipment.presets.${i18nKey}`),
}));

/**
 * The presets for one «نوع کلینیک», and the rest.
 *
 * `matched` is what the screen shows by default; `others` sits behind «نمایش
 * همه», because a clinic may still own a device outside its specialty — a general
 * clinic with an ultrasound. With no type on the clinic, or a type this build's
 * table has nothing for (the registry is server-owned and may run ahead of the
 * client), every preset is `matched`: an empty picker would be worse than a
 * long one.
 */
export function equipmentPresetsFor(clinicType, t) {
  const all = EQUIPMENT_TYPE_PRESETS(t);
  const matched = clinicType ? all.filter((p) => p.clinicTypes.includes(clinicType)) : [];
  if (!matched.length) return { matched: all, others: [], filtered: false };
  return { matched, others: all.filter((p) => !matched.includes(p)), filtered: true };
}

/**
 * What a row should SAY, and in what tone — the urgency first.
 *
 * A device in the workshop says how long it has been there; a device with a
 * lapsed calibration says so before it says «در سرویس», because the second
 * fact is the one somebody has to act on.
 */
export function equipmentStatusView(e, t, formatDigits = (n) => n) {
  if (!e) return { labelFa: '—', tone: 'mute' };
  if (e.status === 'under_repair') {
    const days = Number(e.daysUnderRepair) || 0;
    return {
      labelFa: days
        ? t('operations.equipment.status.underRepairFor', { days: formatDigits(days) })
        : t('operations.equipment.status.underRepair'),
      tone: 'danger',
    };
  }
  return { labelFa: equipmentStatusLabel(e.status, t), tone: equipmentStatusTone(e.status, t) };
}

/** «۱۰ روز», «سررسید گذشته», «پس از ۱۲۰ استفاده» — the due cell, and its tone. */
export function equipmentDueView(e, t, formatDigits = (n) => n) {
  if (!e) return null;
  if (e.alertSeverity === 'overdue') {
    return { labelFa: t('operations.equipment.due.overdue'), tone: 'danger' };
  }
  if (e.nextDueAt) {
    const days = Math.floor((Number(e.nextDueAt) - Date.now()) / DAY);
    if (days <= 0) return { labelFa: t('operations.equipment.due.today'), tone: 'warn' };
    return {
      labelFa: t('operations.equipment.due.inDays', { days: formatDigits(days) }),
      tone: e.alertSeverity === 'due_soon' ? 'warn' : 'mute',
    };
  }
  if (e.nextDueUses != null) {
    return {
      labelFa: t('operations.equipment.due.afterUses', { uses: formatDigits(Math.max(e.nextDueUses, 0)) }),
      tone: e.alertSeverity === 'due_soon' ? 'warn' : 'mute',
    };
  }
  return null;
}

export const ALERT_KIND_LABELS = (t) => ({
  service_due: t('operations.equipment.alerts.serviceDue'),
  calibration_due: t('operations.equipment.alerts.calibrationDue'),
  contract_expiring: t('operations.equipment.alerts.contractExpiring'),
  wear_part: t('operations.equipment.alerts.wearPart'),
  under_repair: t('operations.equipment.status.underRepair'),
  return_due: t('operations.equipment.alerts.returnDue'),
});

export const alertKindLabel = (kind, t) => ALERT_KIND_LABELS(t)[kind] || kind || '';

/**
 * The alert as one sentence: «سرویس اتوکلاو اتاق استریل تا ۱۰ روز دیگر سررسید می‌شود».
 * The whole point of the strip is that it reads without opening anything.
 */
export function alertSentence(alert, t, formatDigits = (n) => n) {
  if (!alert) return '';
  const where = alert.room ? ` ${alert.room}` : '';
  const who = `${alert.equipmentName}${where}`;
  if (alert.kind === 'under_repair') return t('operations.equipment.alerts.sentenceRepair', { who });
  if (alert.kind === 'wear_part') {
    return alert.severity === 'overdue'
      ? t('operations.equipment.alerts.sentenceWearDue', { who })
      : t('operations.equipment.alerts.sentenceWearSoon', { who, uses: formatDigits(Math.max(alert.usesLeft || 0, 0)) });
  }
  const what = alertKindLabel(alert.kind, t);
  if (alert.severity === 'overdue') {
    return t('operations.equipment.alerts.sentenceOverdue', { what, who, days: formatDigits(Math.abs(alert.daysLeft || 0)) });
  }
  if (alert.daysLeft != null) {
    return t('operations.equipment.alerts.sentenceDueSoon', { what, who, days: formatDigits(Math.max(alert.daysLeft, 0)) });
  }
  if (alert.usesLeft != null) {
    return t('operations.equipment.alerts.sentenceDueUses', { what, who, uses: formatDigits(Math.max(alert.usesLeft, 0)) });
  }
  return `${what} · ${who}`;
}

/** The strip's three counters, from the alert list — the strip renders only non-zero ones. */
export function equipmentAlertCounts(alerts = []) {
  const out = { overdue: 0, dueSoon: 0, underRepair: 0, total: 0 };
  for (const a of alerts) {
    if (a.kind === 'under_repair') out.underRepair += 1;
    else if (a.severity === 'overdue') out.overdue += 1;
    else if (a.severity === 'due_soon') out.dueSoon += 1;
  }
  out.total = out.overdue + out.dueSoon + out.underRepair;
  return out;
}

export function equipmentCsv(rows, t, formatDate) {
  return toCsv(
    [
      t('operations.fields.code'), t('operations.equipment.entity'), t('common.fields.type'),
      t('operations.equipment.fields.room'), t('common.fields.status'),
      t('operations.equipment.fields.nextService'), t('operations.equipment.fields.calibration'),
      t('operations.equipment.fields.criticality'), t('operations.equipment.fields.regulatoryId'),
      t('operations.equipment.fields.purchasePriceToman'),
    ],
    rows.map((e) => [
      e.code, e.name, e.typeName || '', e.room || '',
      equipmentStatusLabel(e.status, t),
      e.nextDueAt ? fmtJalaliDay(e.nextDueAt, formatDate) : '',
      e.calibrationDueAt ? fmtJalaliDay(e.calibrationDueAt, formatDate) : '',
      criticalityLabel(e.criticality, t), e.regulatoryId || '',
      String(toDisplay(e.purchasePriceRial, e.currency) ?? 0),
    ]),
  );
}
